import type { AccountListItem, CategoryListItem } from '../api-client';
import { createIncome } from '../api-client';
import { parseAmountInput } from '@lib/amount-parse';
import { todayLocalIsoDate } from '@lib/date-utils';
import { showCategoryPicker } from '../pages/category-picker';
import { renderAccountPicker } from '../pages/account-picker';
import { showToast } from './toast';

export interface IncomeSheetProps {
  accounts: ReadonlyArray<AccountListItem>;
  categories: ReadonlyArray<CategoryListItem>;
  onClose(): void;
  onSuccess(): void;
}

export function openIncomeSheet(root: HTMLElement, props: IncomeSheetProps): void {
  let categoryId: string | null = null;
  let toAccountId: string | null = null;
  // Preserved across re-renders (picker selection re-renders the sheet).
  let amountRaw = '';
  let fromText = '';
  let dateVal = todayLocalIsoDate();

  const categoryName = (): string => {
    const c = categoryId ? props.categories.find((x) => x.id === categoryId) : null;
    return c ? c.name : 'Choose category';
  };
  const accountName = (): string => {
    const a = toAccountId ? props.accounts.find((x) => x.id === toAccountId) : null;
    return a ? a.name : 'Choose account';
  };

  const captureInputs = (): void => {
    const a = root.querySelector<HTMLInputElement>('#income-amount'); if (a) amountRaw = a.value;
    const f = root.querySelector<HTMLInputElement>('#income-from'); if (f) fromText = f.value;
    const d = root.querySelector<HTMLInputElement>('#income-date'); if (d) dateVal = d.value;
  };

  const close = (): void => { root.innerHTML = ''; props.onClose(); };

  const render = (): void => {
    const disabled = (!categoryId || !toAccountId) ? 'disabled' : '';
    root.innerHTML = `
      <div class="sheet-backdrop" id="income-backdrop">
        <div class="sheet income-sheet" role="dialog" aria-modal="true">
          <header class="sheet-header"><h3>Add income</h3></header>
          <div class="sheet-body">
            <label class="sheet-field">
              <span class="sheet-label">Amount</span>
              <input id="income-amount" type="text" inputmode="decimal" value="${escapeAttr(amountRaw)}" placeholder='e.g. 30000 or "£2000"' />
              <small class="sheet-helper">AED by default. Prefix a code/symbol for other currencies (USD, £, €, CHF).</small>
            </label>
            <button class="sheet-row" id="income-category-row" type="button">
              <span class="sheet-label">Category</span>
              <span class="sheet-value">${escapeHtml(categoryName())}</span>
              <span class="sheet-chevron">›</span>
            </button>
            <button class="sheet-row" id="income-account-row" type="button">
              <span class="sheet-label">Received into</span>
              <span class="sheet-value">${escapeHtml(accountName())}</span>
              <span class="sheet-chevron">›</span>
            </button>
            <label class="sheet-field">
              <span class="sheet-label">From (optional)</span>
              <input id="income-from" type="text" value="${escapeAttr(fromText)}" placeholder="e.g. ADNOC, tenant name" />
            </label>
            <label class="sheet-field">
              <span class="sheet-label">Date</span>
              <input id="income-date" type="date" value="${escapeAttr(dateVal)}" max="${todayLocalIsoDate()}" />
            </label>
            <div class="sheet-error" id="income-error" hidden></div>
          </div>
          <footer class="sheet-footer">
            <button class="ghost" id="income-cancel" type="button">Cancel</button>
            <button class="primary" id="income-confirm" type="button" ${disabled}>Record income</button>
          </footer>
        </div>
      </div>`;

    root.querySelector<HTMLElement>('#income-backdrop')!.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) close();
    });
    root.querySelector<HTMLButtonElement>('#income-cancel')!.addEventListener('click', close);

    root.querySelector<HTMLButtonElement>('#income-category-row')!.addEventListener('click', () => {
      captureInputs();
      const pickerOpts: { kind: 'income'; exclude: string[]; currentCategoryId?: string } = {
        kind: 'income', exclude: ['cat-refunds'],
      };
      if (categoryId) pickerOpts.currentCategoryId = categoryId;
      void showCategoryPicker((catId) => { categoryId = catId; render(); }, pickerOpts);
    });

    root.querySelector<HTMLButtonElement>('#income-account-row')!.addEventListener('click', () => {
      captureInputs();
      const pickerMount = document.createElement('div');
      root.appendChild(pickerMount);
      renderAccountPicker(pickerMount, [...props.accounts], {
        onPick: (id) => { toAccountId = id; pickerMount.remove(); render(); },
        onCancel: () => pickerMount.remove(),
      }, { filter: 'asset_receivable', selectedId: toAccountId, title: 'Received into' });
    });

    root.querySelector<HTMLButtonElement>('#income-confirm')!.addEventListener('click', async () => {
      const errorEl = root.querySelector<HTMLElement>('#income-error')!;
      errorEl.hidden = true; errorEl.textContent = '';
      captureInputs();

      const parsed = parseAmountInput(amountRaw);
      if (!parsed.ok) {
        errorEl.textContent = describeParseError(parsed.code); errorEl.hidden = false; return;
      }
      if (!categoryId) { errorEl.textContent = 'Pick a category.'; errorEl.hidden = false; return; }
      if (!toAccountId) { errorEl.textContent = 'Pick an account.'; errorEl.hidden = false; return; }

      try {
        await createIncome({
          category_id: categoryId,
          to_account_id: toAccountId,
          amount: parsed.amount,
          currency: parsed.currency,
          occurred_at: dateVal,
          merchant: fromText.trim() ? fromText.trim() : null,
        });
        showToast('Income recorded');
        root.innerHTML = '';
        props.onSuccess();
      } catch (e) {
        errorEl.textContent = e instanceof Error ? e.message : String(e);
        errorEl.hidden = false;
      }
    });
  };

  render();
}

function describeParseError(code: 'empty' | 'no_amount' | 'unknown_currency' | 'non_positive'): string {
  switch (code) {
    case 'empty':            return 'Enter an amount.';
    case 'no_amount':        return 'Enter a valid number.';
    case 'unknown_currency': return 'Unknown currency code. Use AED, USD, GBP, EUR, or CHF.';
    case 'non_positive':     return 'Amount must be greater than zero.';
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function escapeAttr(s: string): string {
  return escapeHtml(s);
}
